import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { chatStatusKeys, questionKeys } from '~/lib/query-keys';

/**
 * FRONT-786 — dismiss an amber ("needs-you") chat from the Inspector by
 * clearing its dangling clarifying question server-side. Cancelling it makes
 * the chat-status spine re-derive to gray, so the light turns off on every
 * surface (Inspector, launcher, chat header, Files cards) and stays off across
 * reloads/devices. The server also broadcasts the change over SSE.
 *
 * FRONT-715 turned this from the ONLY exit into a manual shortcut: a question
 * now retires itself when it goes stale (the user's next message, or a newer
 * round of questions replacing it), so an amber light left by a typed-past
 * question no longer needs rescuing by hand. This stays for the case it was
 * built for — the user wants the row gone NOW, without sending anything.
 */
export function useDismissChatWaiting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      api.post('/api/questions/cancel', { sessionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatStatusKeys.all });
      qc.invalidateQueries({ queryKey: questionKeys.all });
    },
    onError: (err) => {
      toast.error(`Dismiss failed: ${(err as Error).message}`);
    },
  });
}
